import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PlaceIcon from '@mui/icons-material/Place';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { PolylineF } from '@react-google-maps/api';
import { I18n } from 'aws-amplify/utils';
import dayjs from 'dayjs';
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CurrentPassengerInfoShow } from '@/components/pages/drive/details/currentPassengerInfoShow';
import { useDriveDetailsMapMarkers } from '@/components/pages/drive/details/DriveDetailsMapMarkers';
import { MapZoomControls } from '@/components/pages/drive/details/MapZoomControls';
import { PassengerInfoShow } from '@/components/pages/drive/details/passengerInfoShow';
import { ReservationShow } from '@/components/pages/drive/details/reservationInfoShow';
import { useDriveData } from '@/components/pages/drive/details/useDriveData';
import { useDriveDetailsHelpers } from '@/components/pages/drive/details/useDriveDetailsHelpers';
import { useMapRoutes } from '@/components/pages/drive/details/useMapRoutes';
import { useRouteChangeSubscription } from '@/components/pages/drive/details/useRouteChangeSubscription';
import { useVehicleLocation } from '@/components/pages/drive/details/useVehicleLocation';
import { useZoomButtons } from '@/components/pages/drive/details/useZoomButtons';
import OperationStartPopup from '@/components/pages/drive/popup/operationStartPopup';
import ErrorDialog from '@/components/shared/errorDialog';
import { MbDialog } from '@/components/shared/modules';
import { CODE_MASTER_TYPE } from '@/constants/code-master-type';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import type { Reservation, ReservationState, StationState, TimeInformation } from '@/features/driver/types/driveDetail';
import { ReservationStatusCode } from '@/features/driver/types/driveDetail';
import { formatMoney, getTimelineData, getTimelineDataForReservationInfo } from '@/features/driver/utils/processData';
import GoogleMapWrapper from '@/features/map/components/googleMapWrapper';
import MyGoogleMap from '@/features/map/components/myGoogleMap';
import type { VehicleOperationScheduleType } from '@/features/vehicleOperationSchedule/types/vehicleOperationSchedule';
import { apiPut } from '@/modules/hooks/use-put';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';

export type ReservationInfo = {
  waypointId: string;
  reservationId: number;
  type: number; // 1 乗車 2 降車
};

type SegmentType = {
  id: number;
  path: {
    lat: number;
    lng: number;
  }[];
  color: string;
  zindex: number;
};

const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

const isIpadMini = (): boolean => {
  const ua = navigator.userAgent;
  const isIpad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (!isIpad) return false;

  const shortSide = Math.min(window.screen.width, window.screen.height);

  return shortSide <= 768;
};

const isMobilePhone = (): boolean => {
  const ua = navigator.userAgent;
  return /iPhone|Android/.test(ua);
};

export const getEarliestPickupDatetime = (schedule: VehicleOperationScheduleType): string | null => {
  const validDatetimes = schedule.reservationList
    .map((item) => item.latestEstimatedPickupDatetime)
    .filter((date) => date);

  if (validDatetimes.length === 0) {
    return null;
  }

  const earliestDatetime = validDatetimes
    .map((date) => dayjs(date))
    .reduce((earliest, current) => (current.isBefore(earliest) ? current : earliest));

  return earliestDatetime.format('YYYY-MM-DD HH:mm:ss');
};

export const shouldOpenPreOperationStartPopup = (
  reservationDeadlineDateTime: string | undefined,
  earliestPickupDatetime: string | null,
): boolean => {
  if (!earliestPickupDatetime || !reservationDeadlineDateTime) {
    return true;
  }

  const now = dayjs();
  const earliestPickupDate = dayjs(earliestPickupDatetime);
  const earliestBeforeTwoHours = earliestPickupDate.subtract(2, 'hour');

  return now.isBefore(dayjs(reservationDeadlineDateTime)) || now.isBefore(earliestBeforeTwoHours);
};

export const findReservationForMapDetail = (
  reservationList: Reservation[],
  waypointId: string,
  reservationId: number,
  type: number,
): Reservation | undefined => {
  const detailList = getTimelineData(reservationList).filter((item) => String(item.waypointId) === waypointId);

  const candidates = detailList.flatMap((detail) => (type === 1 ? (detail.pickup ?? []) : (detail.dropoff ?? [])));

  return candidates.find((item) => item.reservationId === reservationId);
};

export const toSinglePickupTimeInformation = (waypointId: string, reservation: Reservation): TimeInformation => ({
  key: 1,
  time: '',
  waypointId: Number(waypointId),
  waypointName: '',
  waypointSequence: '',
  latitude: '',
  longitude: '',
  pickup: [reservation],
  dropoff: [],
});

type DriveDetailsProps = {
  storybookMode?: boolean;
  mockVehicleOperationSchedule?: VehicleOperationScheduleType;
  mockIsStartButtonPressed?: boolean;
  mockShowReminderModal?: boolean;
  mockShowPaymentMethodSelectModal?: boolean;
  mockShowPaymentMethodOnlineModal?: boolean;
  mockShowDropoffConfirmModal?: boolean;
  mockShowRouteChangeModal?: boolean;
  mockShowUserCancelModal?: boolean;
  mockShowAdminUpdateModal?: boolean;
  mockShowCancelErrorModal?: boolean;
  mockPaymentMethodList?: { key: string; value: string }[];
  /** Storybook用: 乗車済み・降車済み表示用の初期状態 */
  mockReservationCompletionState?: ReservationState;
  /** Storybook用: 到着済み表示用の初期状態 */
  mockStationArrivalState?: StationState;
  /** Storybook用: 初期表示する停留所インデックス */
  mockWaypointIndex?: number;
  /** Storybook用: 運航開始前でも全地点（B/C含む）を表示する */
  mockShowAllWaypointsBeforeStart?: boolean;
};

export const DriveDetails: React.FC<DriveDetailsProps> = ({
  storybookMode = false,
  mockVehicleOperationSchedule,
  mockIsStartButtonPressed = false,
  mockShowReminderModal = false,
  mockShowPaymentMethodSelectModal = false,
  mockShowPaymentMethodOnlineModal = false,
  mockShowDropoffConfirmModal = false,
  mockShowRouteChangeModal = false,
  mockShowUserCancelModal = false,
  mockShowAdminUpdateModal = false,
  mockShowCancelErrorModal = false,
  mockPaymentMethodList,
  mockReservationCompletionState,
  mockStationArrivalState,
  mockWaypointIndex,
  mockShowAllWaypointsBeforeStart = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const vehicleOperationScheduleId = sessionStorage.getItem(StorageKeys.VEHICLE_OPERATION_SCHEDULE_ID);
  const vehicleId = sessionStorage.getItem(StorageKeys.VEHICLE_ID);
  // ドライバ管理会社ID
  const serviceType = useServiceType();

  const [preOperationStartPopupOpen, setPreOperationStartPopupOpen] = useState(false);
  const [errorOPen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  /** 縦向き時の情報パネル高さ（dvh）。リサイズハンドルで変更。表示範囲変更機能のため削除しないこと。 */
  const [portraitPanelRatio, setPortraitPanelRatio] = useState<number>(50);
  const [isPortraitResizing, setIsPortraitResizing] = useState<boolean>(false);
  const [showSplitMapView, setShowSplitMapView] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState(false);
  const [passengerInfoShow, setPassengerInfoShow] = useState(false);
  const [isDriveDetailViewOpen, setIsDriveDetailViewOpen] = useState(false);
  const [detailViewType, setDetailViewType] = useState<'pickup' | 'dropoff'>('pickup');
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const [currentPassengerInfoShow, setCurrentPassengerInfoShow] = useState(false);
  const [reservationInfo, setReservationInfo] = useState<ReservationInfo>();
  const [toggle, setToggle] = useState(false);
  const [details, setDetails] = useState<TimeInformation[] | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<string>();

  const handleError = useCallback((message: string) => {
    setErrorMessage(message);
    setErrorOpen(true);
  }, []);

  const {
    vehicleOperationSchedule,
    reservationList,
    detailDisabled,
    isStartButtonPressed,
    setIsStartButtonPressed,
    realTimeDetectionFlag,
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    routeChangeModalShowFromMock,
    newReservationListFromMock,
  } = useDriveData({
    vehicleOperationScheduleId,
    storybookMode,
    mockVehicleOperationSchedule,
    mockIsStartButtonPressed,
    mockShowRouteChangeModal,
    onError: handleError,
  });

  const { location } = useVehicleLocation({
    vehicleId,
    serviceType,
    storybookMode,
    onError: handleError,
  });

  const { routeChangeModalShow, setRouteChangeModalShow, newReservationList } = useRouteChangeSubscription({
    vehicleId,
    vehicleOperationScheduleId,
    serviceType,
    reservationList,
    realTimeDetectionFlag,
    storybookMode,
    routeChangeModalShowFromMock,
    newReservationListFromMock,
    onError: handleError,
  });

  const { routesData, segments, updateRoutesType } = useMapRoutes({
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    reservationList,
    storybookMode,
    onError: handleError,
  });

  const { currentZoom, zoombuttonColor15, zoombuttonColor18, zoombuttonColor20, handleOnZoomChange } = useZoomButtons();

  const { pickupAreaFlg, dropoffAreaFlg, pickupDropoffAreaFlg } = useDriveDetailsHelpers({
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
  });

  const zoom = 14;

  /** 運行エリア（waypoint + 現在地）の重心を地図の中心として使用。現在地が取得できない場合はフォールバック座標を利用。 */
  const mapCenter = useMemo((): google.maps.LatLngLiteral => {
    const positions = [...routesData.map((route) => route.waypointPosition)];
    if (location) positions.push(location);
    if (positions.length === 0) {
      return { lat: 35.5, lng: 139.5 };
    }
    const avgLat = positions.reduce((sum, p) => sum + p.lat, 0) / positions.length;
    const avgLng = positions.reduce((sum, p) => sum + p.lng, 0) / positions.length;
    return { lat: avgLat, lng: avgLng };
  }, [routesData, location]);

  const markerPositionsForMap = useMemo(
    () => [...routesData.map((route) => route.waypointPosition), ...(location ? [location] : [])],
    [routesData, location],
  );

  const handlePopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  function formatDate(datetimeStr: string) {
    if (!datetimeStr) return '';

    return dayjs(datetimeStr).format('YY/MM/DD');
  }

  function convertTimeFormat(dateTimeString: string) {
    if (!dateTimeString) return '';

    return dayjs(dateTimeString).format('HH:mm');
  }

  const allWaypointsBeforeStart = useMemo(() => {
    if (!storybookMode || !mockShowAllWaypointsBeforeStart) {
      return [];
    }
    if (reservationList.length === 0) {
      return [];
    }

    return getTimelineDataForReservationInfo(reservationList);
  }, [mockShowAllWaypointsBeforeStart, reservationList, storybookMode]);

  function getLastNameHiragana(userName: string) {
    const charList = userName.split(' ');
    const lastNameHiragana = charList && charList.length > 0 ? charList[0] : ''; // 获取数组中的第一个元素

    return `${lastNameHiragana}${t('driveDetail.mr')}`;
  }

  const minPickupObject = vehicleOperationSchedule?.reservationList?.reduce((prev, current) => {
    const prevPickupTime = new Date(prev.latestEstimatedPickupDatetime).getTime();
    const currentPickupTime = new Date(current.latestEstimatedPickupDatetime).getTime();

    if (prevPickupTime < currentPickupTime) {
      return prev;
    }
    if (prevPickupTime > currentPickupTime) {
      return current;
    }

    const prevPickupSequence = prev.pickupWaypointSequence ? parseInt(prev.pickupWaypointSequence, 10) : Infinity;
    const currentPickupSequence = current.pickupWaypointSequence
      ? parseInt(current.pickupWaypointSequence, 10)
      : Infinity;

    return prevPickupSequence < currentPickupSequence ? prev : current;
  }, vehicleOperationSchedule.reservationList[0]);

  const maxDropoffObject = vehicleOperationSchedule?.reservationList?.reduce((prev, current) => {
    const prevDropoffTime = new Date(prev.latestEstimatedDropoffDatetime).getTime();
    const currentDropoffTime = new Date(current.latestEstimatedDropoffDatetime).getTime();

    if (prevDropoffTime > currentDropoffTime) {
      return prev;
    }
    if (prevDropoffTime < currentDropoffTime) {
      return current;
    }

    const prevDropoffSequence = prev.dropoffWaypointSequence ? parseInt(prev.dropoffWaypointSequence, 10) : -Infinity;
    const currentDropoffSequence = current.dropoffWaypointSequence
      ? parseInt(current.dropoffWaypointSequence, 10)
      : -Infinity;

    return prevDropoffSequence > currentDropoffSequence ? prev : current;
  }, vehicleOperationSchedule.reservationList[0]);

  const paymentMethodInVehicles = useCodeMaster(CODE_MASTER_TYPE.PAYMENT_METHOD_AT_RESERVATION);

  const detailReservation = useMemo(() => {
    if (reservationList.length === 0) {
      return undefined;
    }

    if (selectedReservationId !== null) {
      const found = reservationList.find((r) => r.reservationId === selectedReservationId);
      if (found) return found;
    }

    const sortedReservations = [...reservationList].sort((a, b) => {
      const pickupA = new Date(a.latestEstimatedPickupDatetime).getTime();
      const pickupB = new Date(b.latestEstimatedPickupDatetime).getTime();
      if (pickupA !== pickupB) {
        return pickupA - pickupB;
      }

      const sequenceA = Number(a.pickupWaypointSequence);
      const sequenceB = Number(b.pickupWaypointSequence);

      return sequenceA - sequenceB;
    });

    return sortedReservations[0];
  }, [reservationList, selectedReservationId]);

  const paymentMethodName = useMemo(() => {
    if (!detailReservation) {
      return '';
    }

    const method = paymentMethodInVehicles.find((item) => item.key === String(detailReservation.paymentMethod));

    return method?.value || '';
  }, [detailReservation, paymentMethodInVehicles]);

  const passengerCountLabel = useMemo(() => {
    if (!detailReservation?.passengerTypeList) {
      return '';
    }

    return detailReservation.passengerTypeList
      .map((passenger) => `${passenger.passengerTypeName} ${passenger.passengerCount}${t('driveDetail.people')}`)
      .join(' ');
  }, [detailReservation, t]);

  const onMarkerClick = useCallback((markerId: string) => {
    setSelectedMarker(markerId);
  }, []);

  const handleOk = () => {
    const { reservationDeadlineDateTime } = vehicleOperationSchedule;
    const earliestPickupDatetime = getEarliestPickupDatetime(vehicleOperationSchedule);
    const shouldOpenPopup = shouldOpenPreOperationStartPopup(reservationDeadlineDateTime, earliestPickupDatetime);

    if (shouldOpenPopup) {
      handleClosePopup();
      setPreOperationStartPopupOpen(true);
    } else {
      handleClosePopup();
      const reservationIdList = vehicleOperationSchedule.reservationList.map((item) => ({
        reservationId: item.reservationId,
      }));

      const body = {
        reservationIdList,
        reservationStatusCode: ReservationStatusCode.VEHICLE_APPROCHING,
        isNotification: false,
      };

      apiPut(ApiNamePaths.reservationsStatus.apiName, ApiNamePaths.reservationsStatus.apiPath, { body })
        .then(() => {
          setIsStartButtonPressed(true);
          setPassengerInfoShow(false);
          const isExist = pickupDropoffInformation.find(
            (item) => item.pickupWaypointId === minPickupObject?.pickupWaypointId,
          );
          if (isExist) {
            onMarkerClick(`${minPickupObject?.pickupWaypointId},${minPickupObject?.pickupWaypointId}`);
          } else {
            onMarkerClick(String(minPickupObject?.pickupWaypointId));
          }
        })
        .catch((error: { message: string }) => {
          setErrorMessage(I18n.get(error.message) as string);
          setErrorOpen(true);
        });
    }
  };

  const handleCloseDialog = () => {
    setRouteChangeModalShow(false);

    window.location.reload();
  };

  const handleCloseErrorDialog = () => {
    setErrorOpen(false);

    if (errorMessage.includes('RSH80025') || errorMessage.includes('RSH80039')) {
      navigate(paths.driveList);
    }
  };

  // ナビボタン
  const handleIconClick = () => {
    const dest = `${minPickupObject?.pickupLatitude},${minPickupObject?.pickupLongitude}`;

    if (isIpadMini()) {
      setShowSplitMapView((prev) => !prev);

      return;
    }

    const daddr = encodeURIComponent(dest);
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${daddr}&directionsmode=driving&avoid=highways,tolls`;

    if (isMobilePhone()) {
      window.location.assign(googleMapsUrl);
      return;
    }

    window.open(googleMapsUrl, '_blank');
  };

  const splitMapEmbedUrl = useMemo(() => {
    if (!minPickupObject?.pickupLatitude || !minPickupObject?.pickupLongitude) return '';
    const dest = `${minPickupObject.pickupLatitude},${minPickupObject.pickupLongitude}`;
    const origin = location
      ? `${location.lat},${location.lng}`
      : routesData.length > 0
        ? `${routesData[0].waypointPosition.lat},${routesData[0].waypointPosition.lng}`
        : dest;

    return `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${origin}&destination=${dest}&mode=driving&avoid=highways|tolls`;
  }, [minPickupObject?.pickupLatitude, minPickupObject?.pickupLongitude, location, routesData]);

  const handleMapDetailButtonClick = useCallback(
    (id: string, reservationId: number, type: number) => {
      setCurrentPassengerInfoShow(true);
      setPassengerInfoShow(false);
      setReservationInfo({ waypointId: id, reservationId, type });
      setToggle((prev) => !prev);
      const reservationItem = findReservationForMapDetail(reservationList, id, reservationId, type);

      if (reservationItem) {
        setDetails([toSinglePickupTimeInformation(id, reservationItem)]);
      }
    },
    [reservationList],
  );

  const markers = useDriveDetailsMapMarkers({
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    pickupAreaFlg,
    dropoffAreaFlg,
    pickupDropoffAreaFlg,
    selectedMarker,
    location,
    onMarkerClick,
    onMapDetailButtonClick: handleMapDetailButtonClick,
    convertTimeFormat,
    getLastNameHiragana,
  });

  const renderMarkers = useCallback(() => markers, [markers]);

  const mapButton = useCallback(
    (controls: Record<string, unknown>) => {
      const handleZoomChange = controls.handleZoomChange as (newZoom: number) => void;
      const handleCenterChange = controls.handleCenterChange as (newCenter: google.maps.LatLngLiteral) => void;

      return (
        <MapZoomControls
          location={location ?? mapCenter}
          currentZoom={currentZoom}
          zoombuttonColor15={zoombuttonColor15}
          zoombuttonColor18={zoombuttonColor18}
          zoombuttonColor20={zoombuttonColor20}
          onZoomChange={(nextZoom) => {
            handleZoomChange(nextZoom);
            handleOnZoomChange(nextZoom);
          }}
          onCenterChange={handleCenterChange}
        />
      );
    },
    [location, mapCenter, currentZoom, zoombuttonColor15, zoombuttonColor18, zoombuttonColor20, handleOnZoomChange],
  );

  const polylineComponents = useMemo(
    () =>
      segments
        .filter((segment) => segment.path?.length > 0)
        .map((segment: SegmentType) => (
          <PolylineF
            key={segment.id}
            path={segment.path}
            options={{
              strokeColor: segment.color,
              strokeOpacity: 1,
              strokeWeight: 5,
              clickable: false,
              draggable: false,
              editable: false,
              visible: true,
              zIndex: segment.zindex,
            }}
          />
        )),
    [segments],
  );

  useEffect(() => {
    if (!isPortraitResizing) {
      return;
    }

    const updateRatio = (clientY: number) => {
      const viewportHeight = window.innerHeight || 1;
      const nextRatio = ((viewportHeight - clientY) / viewportHeight) * 100;
      const clampedRatio = Math.max(30, Math.min(70, nextRatio));
      setPortraitPanelRatio(clampedRatio);
    };

    const handleMouseMove = (event: MouseEvent) => {
      updateRatio(event.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        updateRatio(event.touches[0].clientY);
      }
    };

    const stopResize = () => {
      setIsPortraitResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResize);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', stopResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResize);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopResize);
    };
  }, [isPortraitResizing]);

  /** 縦向き時の情報パネル高さ。CSS で .info-panel / .portrait-resize-handle が参照。表示範囲変更機能用。 */
  const portraitSplitStyle = useMemo(
    () =>
      ({
        '--portrait-panel-ratio': `${portraitPanelRatio}dvh`,
      }) as CSSProperties,
    [portraitPanelRatio],
  );

  return (
    <div className={`split-view-wrapper ${showSplitMapView ? 'split-view-active' : ''}`}>
      <div className="layout-surface" style={portraitSplitStyle}>
        <Box className="map-pane">
          <GoogleMapWrapper>
            <MyGoogleMap
              center={mapCenter}
              zoom={zoom}
              onMarkerClick={onMarkerClick}
              renderMarkers={renderMarkers}
              mapButton={mapButton}
              onZoomChange={handleOnZoomChange}
              markerPositions={markerPositionsForMap}
            >
              {polylineComponents}
            </MyGoogleMap>
          </GoogleMapWrapper>
        </Box>
        <div className="drive-info-column">
          {/* 情報部分の表示範囲を変更するリサイズハンドル。機能削除禁止。 */}
          <button
            type="button"
            className="portrait-resize-handle"
            onMouseDown={() => setIsPortraitResizing(true)}
            onTouchStart={() => setIsPortraitResizing(true)}
            aria-label="Resize map and detail panels"
          >
            <span className="portrait-resize-handle-bar" />
          </button>
          {!isStartButtonPressed && (
            <div className="info-panel">
              {isDriveDetailViewOpen ? (
                <div className="detail-view-container">
                  <div className="detail-view-header-row">
                    <div className="detail-view-pickup-wrap">
                      {detailViewType === 'pickup' ? (
                        <div className="waypoint-card waypoint-card-embark detail-view-pickup-card">
                          <div className="waypoint-card-header">
                            <span className="waypoint-badge waypoint-badge-embark">{t('driveDetail.embark')}</span>
                            <span className="waypoint-clock">
                              {convertTimeFormat(detailReservation?.latestEstimatedPickupDatetime || '')}
                            </span>
                            <span className="waypoint-stop-code">{detailReservation?.pickupWaypointNumber || ''}</span>
                          </div>
                          <div className="waypoint-name-text">{detailReservation?.pickupWaypointName || ''}</div>
                        </div>
                      ) : (
                        <div className="waypoint-card waypoint-card-dropoff detail-view-pickup-card">
                          <div className="waypoint-card-header">
                            <span className="waypoint-badge waypoint-badge-dropoff">{t('driveDetail.disembark')}</span>
                            <span className="waypoint-clock">
                              {convertTimeFormat(detailReservation?.latestEstimatedDropoffDatetime || '')}
                            </span>
                            <span className="waypoint-stop-code">{detailReservation?.dropoffWaypointNumber || ''}</span>
                          </div>
                          <div className="waypoint-name-text">{detailReservation?.dropoffWaypointName || ''}</div>
                        </div>
                      )}
                    </div>
                    <button className="summary-map-card detail-view-map-card" type="button" onClick={handleIconClick}>
                      <PlaceIcon className="summary-map-icon" />
                      <span className="summary-map-text">GoogleMap</span>
                      <span className="summary-map-text">で見る</span>
                    </button>
                  </div>
                  <div className="detail-view-list">
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.reservationId').replace('：', '')}</span>
                      <span className="detail-view-value">{detailReservation?.reservationId || ''}</span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.reservationCount')}</span>
                      <span className="detail-view-value">{passengerCountLabel}</span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.name').replace('：', '')}</span>
                      <span className="detail-view-value">
                        {detailReservation?.userName ? `${detailReservation.userName}${t('driveDetail.mr')}` : ''}
                      </span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.pickupDatetime')}</span>
                      <span className="detail-view-value">
                        {convertTimeFormat(detailReservation?.latestEstimatedPickupDatetime || '')}
                      </span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.pickupWaypointName')}</span>
                      <span className="detail-view-value">{detailReservation?.pickupWaypointName || ''}</span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.dropoffDatetime')}</span>
                      <span className="detail-view-value">
                        {convertTimeFormat(detailReservation?.latestEstimatedDropoffDatetime || '')}
                      </span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.dropoffWaypointName')}</span>
                      <span className="detail-view-value">{detailReservation?.dropoffWaypointName || ''}</span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.price').replace('：', '')}</span>
                      <span className="detail-view-value">{`${formatMoney(detailReservation?.price || 0)}${t('driveDetail.yen')}`}</span>
                    </div>
                    <div className="detail-view-row">
                      <span className="detail-view-label">{t('driveDetail.paymentMethod').replace('：', '')}</span>
                      <span className="detail-view-value">{paymentMethodName}</span>
                    </div>
                    <div className="detail-view-message">
                      <span className="detail-view-label">{t('driveDetail.message').replace('：', '')}</span>
                      <span className="detail-view-message-text">{detailReservation?.message || ''}</span>
                    </div>
                  </div>
                  <div className="detail-view-back-wrap">
                    <Button
                      className="action-button action-button-secondary detail-view-back-button"
                      variant="contained"
                      onClick={() => setIsDriveDetailViewOpen(false)}
                    >
                      {t('driveDetail.back')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="summary-row">
                    <div className="summary-card summary-main-card">
                      <div className="summary-date-text">{formatDate(vehicleOperationSchedule.date)}</div>
                      <div className="summary-meta-row">
                        <span className="summary-meta-item summary-meta-wrap">
                          <PeopleOutlineIcon className="summary-meta-icon" />
                          <span className="summary-meta-item-text">
                            {vehicleOperationSchedule.driveReservationCount}
                            {t('driveDetail.passengerCount')} {vehicleOperationSchedule.drivePassengerCount}
                            {t('driveDetail.people')}
                          </span>
                        </span>
                        <span className="summary-meta-item summary-meta-wrap">
                          <PaidOutlinedIcon className="summary-meta-icon" />
                          <span className="summary-meta-item-text">
                            {t('driveDetail.total')}
                            {vehicleOperationSchedule.drivePrice}
                            {t('driveDetail.yen')}
                          </span>
                        </span>
                        <span className="summary-meta-item summary-meta-route">
                          {vehicleOperationSchedule.operationZoneName}
                          {vehicleOperationSchedule.direction && vehicleOperationSchedule.direction !== '-'
                            ? ` ${vehicleOperationSchedule.direction}`
                            : ''}
                        </span>
                      </div>
                    </div>
                    <button className="summary-map-card" type="button" onClick={handleIconClick}>
                      <PlaceIcon className="summary-map-icon" />
                      <span className="summary-map-text">GoogleMap</span>
                      <span className="summary-map-text">で見る</span>
                    </button>
                  </div>
                  <div className="waypoint-section">
                    {allWaypointsBeforeStart.length > 0 ? (
                      allWaypointsBeforeStart.map((item, index) => {
                        const isDisembark = item.dropoff.length > 0 && item.pickup.length === 0;
                        const isPickupDropoff = item.pickup.length > 0 && item.dropoff.length > 0;
                        const [stopCode, ...nameParts] = (item.waypointName ?? '').split('.');
                        const waypointName = nameParts.length > 0 ? nameParts.join('.') : (item.waypointName ?? '');

                        return (
                          <div key={item.key}>
                            <div
                              className={`waypoint-card ${isDisembark ? 'waypoint-card-dropoff' : 'waypoint-card-embark'}`}
                            >
                              <div className="waypoint-card-header">
                                <span
                                  className={`waypoint-badge ${
                                    isDisembark ? 'waypoint-badge-dropoff' : 'waypoint-badge-embark'
                                  }`}
                                >
                                  {isDisembark ? t('driveDetail.disembark') : t('driveDetail.embark')}
                                </span>
                                <span className="waypoint-clock">{convertTimeFormat(item.time ?? '')}</span>
                                <span className="waypoint-stop-code">{stopCode || ''}</span>
                              </div>
                              <div className="waypoint-name-text">
                                {waypointName}
                                {isPickupDropoff ? `（${t('driveDetail.embark')}・${t('driveDetail.disembark')}）` : ''}
                              </div>
                            </div>
                            <div className="waypoint-arrow">
                              {index < allWaypointsBeforeStart.length - 1 ? '↓' : ''}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <button
                          type="button"
                          className="waypoint-card waypoint-card-embark waypoint-card-clickable"
                          onClick={() => {
                            setSelectedReservationId(minPickupObject?.reservationId ?? null);
                            setDetailViewType('pickup');
                            setPassengerInfoShow(false);
                            setCurrentPassengerInfoShow(false);
                            setDetails(null);
                            setIsDriveDetailViewOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedReservationId(minPickupObject?.reservationId ?? null);
                              setDetailViewType('pickup');
                              setPassengerInfoShow(false);
                              setCurrentPassengerInfoShow(false);
                              setDetails(null);
                              setIsDriveDetailViewOpen(true);
                            }
                          }}
                        >
                          <div className="waypoint-card-header">
                            <span className="waypoint-badge waypoint-badge-embark">{t('driveDetail.embark')}</span>
                            <span className="waypoint-clock">
                              {convertTimeFormat(minPickupObject?.latestEstimatedPickupDatetime)}
                            </span>
                            <span className="waypoint-stop-code">{minPickupObject.pickupWaypointNumber}</span>
                          </div>
                          <div className="waypoint-name-text">{minPickupObject.pickupWaypointName}</div>
                        </button>
                        <div className="waypoint-arrow">{minPickupObject.pickupWaypointNumber ? '↓' : ''}</div>
                        <button
                          type="button"
                          className="waypoint-card waypoint-card-dropoff waypoint-card-clickable"
                          onClick={() => {
                            setSelectedReservationId(maxDropoffObject?.reservationId ?? null);
                            setDetailViewType('dropoff');
                            setPassengerInfoShow(false);
                            setCurrentPassengerInfoShow(false);
                            setDetails(null);
                            setIsDriveDetailViewOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedReservationId(maxDropoffObject?.reservationId ?? null);
                              setDetailViewType('dropoff');
                              setPassengerInfoShow(false);
                              setCurrentPassengerInfoShow(false);
                              setDetails(null);
                              setIsDriveDetailViewOpen(true);
                            }
                          }}
                        >
                          <div className="waypoint-card-header">
                            <span className="waypoint-badge waypoint-badge-dropoff">{t('driveDetail.disembark')}</span>
                            <span className="waypoint-clock">
                              {convertTimeFormat(maxDropoffObject?.latestEstimatedDropoffDatetime)}
                            </span>
                            <span className="waypoint-stop-code">{maxDropoffObject.dropoffWaypointNumber}</span>
                          </div>
                          <div className="waypoint-name-text">{maxDropoffObject.dropoffWaypointName}</div>
                        </button>
                      </>
                    )}
                  </div>
                  <div className="group-details-label action-button-group">
                    <Button
                      className="action-button action-button-secondary"
                      variant="contained"
                      disabled={detailDisabled}
                      onClick={() => navigate(paths.driveList)}
                    >
                      {t('driveDetail.back')}
                    </Button>
                    <Button
                      className="action-button action-button-primary"
                      variant="contained"
                      disabled={detailDisabled}
                      onClick={handlePopup}
                    >
                      {t('driveDetail.operationStart')}
                    </Button>
                  </div>
                </>
              )}
              {showPopup && <OperationStartPopup handleOk={handleOk} onClose={handleClosePopup} isShow={showPopup} />}
            </div>
          )}
          <ReservationShow
            reservationList={reservationList}
            isShow={isStartButtonPressed}
            vehicleOperationScheduleId={vehicleOperationScheduleId || ''}
            operationDate={vehicleOperationSchedule.date}
            operationZoneName={vehicleOperationSchedule.operationZoneName}
            direction={vehicleOperationSchedule.direction}
            driveReservationCount={vehicleOperationSchedule.driveReservationCount}
            drivePassengerCount={vehicleOperationSchedule.drivePassengerCount}
            drivePrice={vehicleOperationSchedule.drivePrice}
            onMarkerClick={onMarkerClick}
            currentPassengerInfoShow={currentPassengerInfoShow}
            currentReservation={reservationInfo}
            toggle={toggle}
            updateRoutesType={updateRoutesType}
            isDropoffMode={
              reservationList.length > 0 &&
              reservationList.every((r) => Number(r.status) >= Number(ReservationStatusCode.RIDING))
            }
            mockShowReminderModal={mockShowReminderModal}
            mockShowPaymentMethodSelectModal={mockShowPaymentMethodSelectModal}
            mockShowPaymentMethodOnlineModal={mockShowPaymentMethodOnlineModal}
            mockShowDropoffConfirmModal={mockShowDropoffConfirmModal}
            mockShowUserCancelModal={mockShowUserCancelModal}
            mockShowAdminUpdateModal={mockShowAdminUpdateModal}
            mockShowCancelErrorModal={mockShowCancelErrorModal}
            mockPaymentMethodList={mockPaymentMethodList}
            mockInitialReservationCompletionState={mockReservationCompletionState}
            mockInitialStationArrivalState={mockStationArrivalState}
            mockInitialWaypointIndex={mockWaypointIndex}
          />
          <PassengerInfoShow reservation={reservationList} isShow={passengerInfoShow && !isDriveDetailViewOpen} />
          {!isStartButtonPressed && details && (
            <CurrentPassengerInfoShow data={details} isShow={currentPassengerInfoShow && !isDriveDetailViewOpen} />
          )}
          {!isStartButtonPressed && !isDriveDetailViewOpen && (
            <Box className="u-hidden">
              <Button
                variant="text"
                sx={{
                  width: 80,
                  justifyContent: 'flex-start',
                  color: 'black',
                  clipPath: 'polygon(0 0, 100% 0%, 60% 100%, 0% 100%)',
                  backgroundColor: 'lightgrey !important',
                  fontSize: '14px',
                }}
                disabled={detailDisabled}
                onClick={() => {
                  setPassengerInfoShow(false);
                  setCurrentPassengerInfoShow(false);
                  setDetails(null);
                  setDetailViewType('pickup');
                  setIsDriveDetailViewOpen(true);
                }}
              >
                {t('driveDetail.detail')}
                <br />
                {t('driveDetail.showMore')}
              </Button>
            </Box>
          )}
        </div>{' '}
        {errorOPen ? (
          <ErrorDialog
            open={errorOPen}
            errorMessage={errorMessage}
            onClose={() => {
              handleCloseErrorDialog();
            }}
          />
        ) : null}
        <MbDialog
          open={preOperationStartPopupOpen}
          content={t('driveDetail.preOperationStartPopupMessage')}
          okLabel={t('driveDetail.yes')}
          onOk={() => {
            setPreOperationStartPopupOpen(false);
          }}
          onClose={() => setPreOperationStartPopupOpen(false)}
        />
        {/* ルート変更ポップアップ */}
        <Dialog
          open={routeChangeModalShow}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.13)',
              width: '90%',
              maxWidth: '480px',
              margin: '16px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: '#00b06b',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            <Typography
              sx={{ fontSize: 24, fontWeight: 700, color: '#ffffff', textAlign: 'center', lineHeight: '33px' }}
            >
              {t('driveDetail.newCarText')}
            </Typography>
          </Box>
          <Box sx={{ color: '#64748b', fontSize: 18, lineHeight: 1.4, textAlign: 'center', letterSpacing: '0.9px' }}>
            <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit', letterSpacing: 'inherit' }}>
              {t('driveDetail.notificationMessage1')}
            </Typography>
            <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit', letterSpacing: 'inherit' }}>
              {t('driveDetail.notificationMessage2')}
            </Typography>
          </Box>
          {newReservationList && newReservationList.length > 0 && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '20px',
                  py: '16px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#64748b' }}>{t('driveDetail.name')}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {newReservationList[0]?.userName.split(' ')[0]}
                  {t('driveDetail.mr')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '20px',
                  py: '16px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#64748b' }}>{t('driveDetail.pickupDatetime')}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {dayjs(newReservationList[0]?.latestEstimatedPickupDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '20px',
                  py: '16px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#64748b' }}>{t('driveDetail.pickupWaypoint')}</Typography>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      borderRadius: '4px',
                      px: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0369a1', lineHeight: '19.2px' }}>
                      {newReservationList[0]?.pickupWaypointNumber}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                    {newReservationList[0]?.pickupWaypointName}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '20px',
                  py: '16px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#64748b' }}>{t('driveDetail.dropoffDatetime')}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {dayjs(newReservationList[0]?.latestEstimatedDropoffDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '20px',
                  py: '16px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#64748b' }}>{t('driveDetail.dropoffWaypoint')}</Typography>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      borderRadius: '4px',
                      px: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0369a1', lineHeight: '19.2px' }}>
                      {newReservationList[0]?.dropoffWaypointNumber}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                    {newReservationList[0]?.dropoffWaypointName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            component="button"
            onClick={() => {
              handleCloseDialog();
            }}
            sx={{
              width: '100%',
              height: 132,
              borderRadius: '12px',
              backgroundColor: '#003ecf',
              boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
              border: 'none',
              color: '#ffffff',
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.4,
              letterSpacing: '1.6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#0032a8' },
            }}
          >
            {t('driveDetail.yes')}
          </Box>
        </Dialog>
      </div>
      {showSplitMapView && (
        <div className="split-view-map-panel">
          <div className="split-view-map-header">
            <span className="split-view-map-title">Google Maps</span>
            <button
              type="button"
              className="split-view-map-close"
              onClick={() => setShowSplitMapView(false)}
              aria-label="Close map"
            >
              ✕
            </button>
          </div>
          <iframe
            className="split-view-map-iframe"
            src={splitMapEmbedUrl}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Maps"
          />
        </div>
      )}
    </div>
  );
};
